import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Visibility, CheckCircle, Warning } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'in_progress', label: 'In Progress', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'approved', label: 'Approved', color: 'primary' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
]

const PRIORITY_OPTIONS = [
  { value: 'routine', label: 'Routine', color: 'default' },
  { value: 'urgent', label: 'Urgent', color: 'warning' },
  { value: 'stat', label: 'STAT', color: 'error' },
]

const CATEGORY_OPTIONS = [
  { value: 'hematology', label: 'Hematology' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'microbiology', label: 'Microbiology' },
  { value: 'immunology', label: 'Immunology' },
  { value: 'urinalysis', label: 'Urinalysis' },
  { value: 'serology', label: 'Serology' },
  { value: 'pathology', label: 'Pathology' },
  { value: 'other', label: 'Other' },
]

const LabResultsList = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedResult, setSelectedResult] = useState(null)
  const [criticalCount, setCriticalCount] = useState(0)
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_phone: '',
    patient_gender: '',
    test_name: '',
    test_code: '',
    test_category: 'other',
    priority: 'routine',
    sample_id: '',
    sample_type: '',
    result_summary: '',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [resultsRes, criticalRes, pendingRes] = await Promise.all([
        api.get('/clinical/lab-results/'),
        api.get('/clinical/lab-results/critical/'),
        api.get('/clinical/lab-results/pending_approval/'),
      ])
      setResults(resultsRes.data.results || resultsRes.data || [])
      setCriticalCount((criticalRes.data.results || criticalRes.data || []).length)
      setPendingApprovalCount((pendingRes.data.results || pendingRes.data || []).length)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load lab results')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedResult(null)
    setFormData({
      patient_id: '',
      patient_name: '',
      patient_phone: '',
      patient_gender: '',
      test_name: '',
      test_code: '',
      test_category: 'other',
      priority: 'routine',
      sample_id: '',
      sample_type: '',
      result_summary: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (result) => {
    setSelectedResult(result)
    setFormData({
      patient_id: result.patient_id || '',
      patient_name: result.patient_name || '',
      patient_phone: result.patient_phone || '',
      patient_gender: result.patient_gender || '',
      test_name: result.test_name || '',
      test_code: result.test_code || '',
      test_category: result.test_category || 'other',
      priority: result.priority || 'routine',
      sample_id: result.sample_id || '',
      sample_type: result.sample_type || '',
      result_summary: result.result_summary || '',
      notes: result.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = async (result) => {
    try {
      const response = await api.get(`/clinical/lab-results/${result.id}/`)
      setSelectedResult(response.data)
      setDetailOpen(true)
    } catch (error) {
      toast.error('Failed to load result details')
    }
  }

  const handleSave = async () => {
    if (!formData.patient_id || !formData.patient_name || !formData.test_name) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedResult) {
        await api.patch(`/clinical/lab-results/${selectedResult.id}/`, formData)
        toast.success('Lab result updated')
      } else {
        await api.post('/clinical/lab-results/', formData)
        toast.success('Lab result created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving lab result:', error)
      toast.error(error.response?.data?.error || 'Failed to save lab result')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (result) => {
    if (!window.confirm(`Delete lab result for ${result.patient_name}?`)) return
    try {
      await api.delete(`/clinical/lab-results/${result.id}/`)
      toast.success('Lab result deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting lab result:', error)
      toast.error('Failed to delete lab result')
    }
  }

  const handleComplete = async (result) => {
    try {
      await api.post(`/clinical/lab-results/${result.id}/complete/`)
      toast.success('Lab result marked as completed')
      fetchData()
      setDetailOpen(false)
    } catch (error) {
      toast.error('Failed to complete lab result')
    }
  }

  const handleApprove = async (result) => {
    try {
      await api.post(`/clinical/lab-results/${result.id}/approve/`)
      toast.success('Lab result approved')
      fetchData()
      setDetailOpen(false)
    } catch (error) {
      toast.error('Failed to approve lab result')
    }
  }

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status)
    return option?.color || 'default'
  }

  const getPriorityColor = (priority) => {
    const option = PRIORITY_OPTIONS.find((o) => o.value === priority)
    return option?.color || 'default'
  }

  const columns = [
    { field: 'patient_name', headerName: 'Patient Name', sortable: true },
    { field: 'patient_id', headerName: 'Patient ID' },
    { field: 'test_name', headerName: 'Test Name', sortable: true },
    {
      field: 'test_category',
      headerName: 'Category',
      renderCell: (v) => <Chip label={v} size="small" sx={{ textTransform: 'capitalize' }} />,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      renderCell: (v) => (
        <Chip
          label={v}
          size="small"
          color={getPriorityColor(v)}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (v) => (
        <Chip
          label={v?.replace('_', ' ')}
          size="small"
          color={getStatusColor(v)}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'critical_values',
      headerName: 'Critical',
      renderCell: (v) =>
        v ? <Chip label="Critical" size="small" color="error" icon={<Warning />} /> : null,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      renderCell: (v) => (v ? new Date(v).toLocaleDateString() : '-'),
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Lab Results"
        subtitle="Manage laboratory test results and approvals"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Clinical', path: '/clinical/lab-results' },
          { label: 'Lab Results' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAdd}>
            Add Lab Result
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {criticalCount > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="error" icon={<Warning />}>
              {criticalCount} result(s) with critical values require attention
            </Alert>
          </Grid>
        )}
        {pendingApprovalCount > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="info" icon={<CheckCircle />}>
              {pendingApprovalCount} result(s) pending approval
            </Alert>
          </Grid>
        )}
      </Grid>

      <Card>
        <DataTable
          columns={columns}
          data={results}
          loading={loading}
          searchPlaceholder="Search lab results..."
          emptyMessage="No lab results found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedResult ? 'Edit Lab Result' : 'Add Lab Result'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedResult ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Patient ID"
              value={formData.patient_id}
              onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Patient Name"
              value={formData.patient_name}
              onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.patient_phone}
              onChange={(e) => setFormData({ ...formData, patient_phone: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.patient_gender}
                onChange={(e) => setFormData({ ...formData, patient_gender: e.target.value })}
                label="Gender"
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Test Name"
              value={formData.test_name}
              onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Test Code"
              value={formData.test_code}
              onChange={(e) => setFormData({ ...formData, test_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.test_category}
                onChange={(e) => setFormData({ ...formData, test_category: e.target.value })}
                label="Category"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Priority"
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Sample ID"
              value={formData.sample_id}
              onChange={(e) => setFormData({ ...formData, sample_id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Sample Type"
              placeholder="e.g., Blood, Urine"
              value={formData.sample_type}
              onChange={(e) => setFormData({ ...formData, sample_type: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Result Summary"
              value={formData.result_summary}
              onChange={(e) => setFormData({ ...formData, result_summary: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Lab Result Details"
        maxWidth="md"
        actions={
          <>
            {selectedResult?.status === 'pending' && (
              <Button color="info" onClick={() => handleComplete(selectedResult)}>
                Mark Complete
              </Button>
            )}
            {selectedResult?.status === 'completed' && (
              <Button color="success" onClick={() => handleApprove(selectedResult)}>
                Approve
              </Button>
            )}
            <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
          </>
        }
      >
        {selectedResult && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Patient Name</Box>
                <Box fontWeight={600}>{selectedResult.patient_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Patient ID</Box>
                <Box fontWeight={600}>{selectedResult.patient_id}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Test Name</Box>
                <Box fontWeight={600}>{selectedResult.test_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Category</Box>
                <Box fontWeight={600} textTransform="capitalize">{selectedResult.test_category}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Status</Box>
                <Chip
                  label={selectedResult.status?.replace('_', ' ')}
                  size="small"
                  color={getStatusColor(selectedResult.status)}
                  sx={{ textTransform: 'capitalize', mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Priority</Box>
                <Chip
                  label={selectedResult.priority}
                  size="small"
                  color={getPriorityColor(selectedResult.priority)}
                  sx={{ textTransform: 'capitalize', mt: 0.5 }}
                />
              </Box>
            </Grid>
            {selectedResult.critical_values && (
              <Grid item xs={12}>
                <Alert severity="error" icon={<Warning />}>
                  This result contains critical values
                </Alert>
              </Grid>
            )}
            {selectedResult.sample_id && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Sample ID</Box>
                  <Box fontWeight={600}>{selectedResult.sample_id}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.sample_type && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Sample Type</Box>
                  <Box fontWeight={600}>{selectedResult.sample_type}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.result_summary && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Result Summary</Box>
                  <Box>{selectedResult.result_summary}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.performed_by_name && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Performed By</Box>
                  <Box fontWeight={600}>{selectedResult.performed_by_name}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.approved_by_name && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Approved By</Box>
                  <Box fontWeight={600}>{selectedResult.approved_by_name}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default LabResultsList
